import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Webhook,
  Clock,
  Play,
  Globe,
  Code,
  Sparkles,
  MessageSquare,
  MessageCircle,
  GitBranch,
  Network,
  Timer,
  Save,
  Trash2,
  ZoomIn,
  ZoomOut,
  Maximize2,
  GripVertical,
  MousePointerClick,
  Keyboard,
  FileSearch,
  Camera,
  Upload,
  Hourglass,
  ArrowDownUp,
  ListFilter,
  Terminal,
  ImagePlus,
  Paintbrush,
  Tags,
  Shuffle,
  Database,
  Merge,
  Filter,
  Bot,
  ClipboardPlus,
  Repeat,
  RotateCcw,
  ShieldAlert,
} from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import {
  WorkflowNode,
  WorkflowConnection,
  WorkflowDefinition,
  WorkflowNodeType,
  NODE_DEFINITIONS,
  createNode,
  getNodeDefinition,
  Position,
} from '../helpers/workflowTypes';
import { WorkflowNodeComponent } from './WorkflowNode';
import { WorkflowNodeConfig } from './WorkflowNodeConfig';
import styles from './WorkflowBuilder.module.css';

// Icon mapping
const ICONS: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  Webhook, Clock, Play, Globe, Code, Sparkles, MessageSquare, MessageCircle,
  GitBranch, Network, Timer, MousePointerClick, Keyboard, FileSearch, Camera,
  Upload, Hourglass, ArrowDownUp, ListFilter, Terminal, ImagePlus, Paintbrush,
  Tags, Shuffle, Database, Merge, Filter, Bot, ClipboardPlus, Repeat,
  RotateCcw, ShieldAlert,
};

interface WorkflowBuilderProps {
  workflow?: WorkflowDefinition;
  onSave?: (workflow: WorkflowDefinition) => void;
  onChange?: (workflow: WorkflowDefinition) => void;
}

export const WorkflowBuilder: React.FC<WorkflowBuilderProps> = ({
  workflow: initialWorkflow,
  onSave,
  onChange,
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [nodes, setNodes] = useState<WorkflowNode[]>(initialWorkflow?.nodes || []);
  const [connections, setConnections] = useState<WorkflowConnection[]>(
    initialWorkflow?.connections || [],
  );
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(new Set());
  const [workflowName, setWorkflowName] = useState(initialWorkflow?.name || 'New Workflow');
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState<Position>({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<Position>({ x: 0, y: 0 });
  const [connectingFrom, setConnectingFrom] = useState<{ nodeId: string; handle: string } | null>(
    null,
  );
  const [draggedNodeType, setDraggedNodeType] = useState<WorkflowNodeType | null>(null);
  // Marquee (rubber-band) selection
  const [marquee, setMarquee] = useState<{ startX: number; startY: number; curX: number; curY: number } | null>(null);

  const selectedNodeId = selectedNodeIds.size === 1 ? [...selectedNodeIds][0] : null;
  const selectedNode = selectedNodeId ? nodes.find((n) => n.id === selectedNodeId) : undefined;

  // Notify parent of changes
  const notifyChange = useCallback(
    (newNodes: WorkflowNode[], newConnections: WorkflowConnection[]) => {
      if (onChange) {
        onChange({
          id: initialWorkflow?.id || crypto.randomUUID(),
          name: workflowName,
          nodes: newNodes,
          connections: newConnections,
          createdAt: initialWorkflow?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }
    },
    [onChange, initialWorkflow, workflowName],
  );

  // Handle node drag from palette
  const handleDragStart = (e: React.DragEvent, nodeType: WorkflowNodeType) => {
    setDraggedNodeType(nodeType);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedNodeType || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const position: Position = {
      x: (e.clientX - rect.left - pan.x) / zoom,
      y: (e.clientY - rect.top - pan.y) / zoom,
    };

    const newNode = createNode(draggedNodeType, position);
    const newNodes = [...nodes, newNode];
    setNodes(newNodes);
    setSelectedNodeIds(new Set([newNode.id]));
    setDraggedNodeType(null);
    notifyChange(newNodes, connections);
  };

  // Handle node movement
  const handleNodeMove = useCallback(
    (nodeId: string, newPosition: Position) => {
      const newNodes = nodes.map((n) => (n.id === nodeId ? { ...n, position: newPosition } : n));
      setNodes(newNodes);
    },
    [nodes],
  );

  const handleNodeMoveEnd = useCallback(() => {
    notifyChange(nodes, connections);
  }, [nodes, connections, notifyChange]);

  // Handle node selection
  const handleNodeSelect = useCallback((nodeId: string, e?: React.MouseEvent) => {
    if (e?.shiftKey) {
      // Shift+click: toggle node in multi-selection
      setSelectedNodeIds((prev) => {
        const next = new Set(prev);
        if (next.has(nodeId)) next.delete(nodeId);
        else next.add(nodeId);
        return next;
      });
    } else {
      setSelectedNodeIds(new Set([nodeId]));
    }
  }, []);

  // Handle node deletion
  const handleNodeDelete = useCallback(
    (nodeId: string) => {
      const newNodes = nodes.filter((n) => n.id !== nodeId);
      const newConnections = connections.filter(
        (c) => c.sourceNodeId !== nodeId && c.targetNodeId !== nodeId,
      );
      setNodes(newNodes);
      setConnections(newConnections);
      setSelectedNodeIds((prev) => { const next = new Set(prev); next.delete(nodeId); return next; });
      notifyChange(newNodes, newConnections);
    },
    [nodes, connections, notifyChange],
  );

  // Handle connection creation
  const handleConnectionStart = useCallback((nodeId: string, handle: string) => {
    setConnectingFrom({ nodeId, handle });
  }, []);

  const handleConnectionEnd = useCallback(
    (nodeId: string, handle: string) => {
      if (!connectingFrom || connectingFrom.nodeId === nodeId) {
        setConnectingFrom(null);
        return;
      }

      // Check if connection already exists
      const exists = connections.some(
        (c) =>
          c.sourceNodeId === connectingFrom.nodeId &&
          c.sourceHandle === connectingFrom.handle &&
          c.targetNodeId === nodeId,
      );

      if (!exists) {
        const newConnection: WorkflowConnection = {
          id: crypto.randomUUID(),
          sourceNodeId: connectingFrom.nodeId,
          sourceHandle: connectingFrom.handle,
          targetNodeId: nodeId,
          targetHandle: handle,
        };
        const newConnections = [...connections, newConnection];
        setConnections(newConnections);
        notifyChange(nodes, newConnections);
      }

      setConnectingFrom(null);
    },
    [connectingFrom, connections, nodes, notifyChange],
  );

  // Handle connection deletion
  const handleConnectionDelete = useCallback(
    (connectionId: string) => {
      const newConnections = connections.filter((c) => c.id !== connectionId);
      setConnections(newConnections);
      notifyChange(nodes, newConnections);
    },
    [connections, nodes, notifyChange],
  );

  // Handle node config change
  const handleNodeConfigChange = useCallback(
    (nodeId: string, config: WorkflowNode['config']) => {
      const newNodes = nodes.map((n) => (n.id === nodeId ? { ...n, config } : n));
      setNodes(newNodes);
      notifyChange(newNodes, connections);
    },
    [nodes, connections, notifyChange],
  );

  // Handle node name change
  const handleNodeNameChange = useCallback(
    (nodeId: string, name: string) => {
      const newNodes = nodes.map((n) => (n.id === nodeId ? { ...n, name } : n));
      setNodes(newNodes);
      notifyChange(newNodes, connections);
    },
    [nodes, connections, notifyChange],
  );

  // Canvas panning & marquee selection
  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    if (e.target === canvasRef.current || (e.target as HTMLElement).classList.contains(styles.canvasContent)) {
      if (e.button === 1 || e.altKey) {
        // Middle click or Alt+click → pan
        setIsPanning(true);
        setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      } else if (e.button === 0) {
        // Left click on empty canvas → start marquee selection
        if (!e.shiftKey) setSelectedNodeIds(new Set());
        const rect = canvasRef.current!.getBoundingClientRect();
        const cx = (e.clientX - rect.left - pan.x) / zoom;
        const cy = (e.clientY - rect.top - pan.y) / zoom;
        setMarquee({ startX: cx, startY: cy, curX: cx, curY: cy });
      }
    }
  };

  const handleCanvasMouseMove = (e: React.MouseEvent) => {
    if (isPanning) {
      setPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
    } else if (marquee && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const cx = (e.clientX - rect.left - pan.x) / zoom;
      const cy = (e.clientY - rect.top - pan.y) / zoom;
      setMarquee((m) => m ? { ...m, curX: cx, curY: cy } : null);
    }
  };

  const handleCanvasMouseUp = () => {
    if (marquee) {
      // Select all nodes within the marquee rectangle
      const left = Math.min(marquee.startX, marquee.curX);
      const right = Math.max(marquee.startX, marquee.curX);
      const top = Math.min(marquee.startY, marquee.curY);
      const bottom = Math.max(marquee.startY, marquee.curY);
      // Only select if the marquee has meaningful size (>5px drag)
      if (right - left > 5 || bottom - top > 5) {
        const NODE_W = 200, NODE_H = 80;
        const hit = nodes.filter((n) => {
          const nx = n.position.x, ny = n.position.y;
          return nx + NODE_W > left && nx < right && ny + NODE_H > top && ny < bottom;
        });
        if (hit.length > 0) {
          setSelectedNodeIds(new Set(hit.map((n) => n.id)));
        }
      }
      setMarquee(null);
    }
    setIsPanning(false);
  };

  // Fit all nodes into the visible canvas area
  const fitToView = useCallback(() => {
    if (nodes.length === 0 || !canvasRef.current) return;
    const NODE_W = 200, NODE_H = 80, PAD = 40;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const n of nodes) {
      minX = Math.min(minX, n.position.x);
      minY = Math.min(minY, n.position.y);
      maxX = Math.max(maxX, n.position.x + NODE_W);
      maxY = Math.max(maxY, n.position.y + NODE_H);
    }
    const rect = canvasRef.current.getBoundingClientRect();
    const contentW = maxX - minX + PAD * 2;
    const contentH = maxY - minY + PAD * 2;
    const newZoom = Math.min(Math.max(rect.width / contentW, 0.5), Math.min(rect.height / contentH, 1.5));
    const scaledW = contentW * newZoom;
    const scaledH = contentH * newZoom;
    setZoom(newZoom);
    setPan({
      x: (rect.width - scaledW) / 2 - (minX - PAD) * newZoom,
      y: (rect.height - scaledH) / 2 - (minY - PAD) * newZoom,
    });
  }, [nodes]);

  // Auto-fit on initial load when there are nodes
  useEffect(() => {
    if ((initialWorkflow?.nodes?.length || 0) > 0) {
      // Small delay to let canvas mount and get dimensions
      const timer = setTimeout(fitToView, 50);
      return () => clearTimeout(timer);
    }
  }, []); // Only on mount

  // Zoom controls
  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.1, 2));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.1, 0.5));
  const handleZoomReset = () => {
    fitToView();
  };

  // Save workflow
  const handleSave = () => {
    if (onSave) {
      onSave({
        id: initialWorkflow?.id || crypto.randomUUID(),
        name: workflowName,
        nodes,
        connections,
        createdAt: initialWorkflow?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    }
  };

  // Render connections as SVG lines
  const renderConnections = () => {
    return connections.map((conn) => {
      const sourceNode = nodes.find((n) => n.id === conn.sourceNodeId);
      const targetNode = nodes.find((n) => n.id === conn.targetNodeId);
      if (!sourceNode || !targetNode) return null;

      const sourceDef = getNodeDefinition(sourceNode.type);
      const outputIndex = sourceDef?.outputLabels?.indexOf(conn.sourceHandle) ?? 0;
      const outputCount = sourceDef?.outputs || 1;

      // Calculate positions (approximate - actual positions depend on node size)
      const sourceX = sourceNode.position.x + 200; // Right side of node
      const sourceY = sourceNode.position.y + 40 + outputIndex * 24; // Offset for each output
      const targetX = targetNode.position.x; // Left side of node
      const targetY = targetNode.position.y + 40;

      // Bezier curve control points
      const midX = (sourceX + targetX) / 2;

      return (
        <g
          key={conn.id}
          onClick={() => handleConnectionDelete(conn.id)}
          className={styles.connection}
        >
          <path
            d={`M ${sourceX} ${sourceY} C ${midX} ${sourceY}, ${midX} ${targetY}, ${targetX} ${targetY}`}
            fill="none"
            stroke="var(--color-primary)"
            strokeWidth="2"
            className={styles.connectionPath}
          />
          <path
            d={`M ${sourceX} ${sourceY} C ${midX} ${sourceY}, ${midX} ${targetY}, ${targetX} ${targetY}`}
            fill="none"
            stroke="transparent"
            strokeWidth="20"
            style={{ cursor: 'pointer' }}
          />
        </g>
      );
    });
  };

  return (
    <div className={styles.builder}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <Input
          value={workflowName}
          onChange={(e) => setWorkflowName(e.target.value)}
          className={styles.nameInput}
          placeholder="Workflow name..."
        />
        <div className={styles.toolbarActions}>
          <Button size="icon-sm" variant="ghost" onClick={handleZoomOut} title="Zoom out">
            <ZoomOut size={16} />
          </Button>
          <span className={styles.zoomLevel}>{Math.round(zoom * 100)}%</span>
          <Button size="icon-sm" variant="ghost" onClick={handleZoomIn} title="Zoom in">
            <ZoomIn size={16} />
          </Button>
          <Button size="icon-sm" variant="ghost" onClick={handleZoomReset} title="Reset view">
            <Maximize2 size={16} />
          </Button>
          <div className={styles.toolbarDivider} />
          <Button onClick={handleSave}>
            <Save size={16} />
            Save
          </Button>
        </div>
      </div>

      <div className={styles.main}>
        {/* Node Palette */}
        <div className={styles.palette}>
          <h3 className={styles.paletteTitle}>Nodes</h3>

          {([
            { key: 'trigger', label: 'Triggers' },
            { key: 'browser', label: 'Browser' },
            { key: 'ai', label: 'AI' },
            { key: 'data', label: 'Data' },
            { key: 'action', label: 'Actions' },
            { key: 'taskingbot', label: 'TaskingBot' },
            { key: 'logic', label: 'Logic' },
          ] as const).map((cat) => (
            <div key={cat.key} className={styles.paletteSection}>
              <h4>{cat.label}</h4>
              {NODE_DEFINITIONS.filter((d) => d.category === cat.key).map((def) => {
                const Icon = ICONS[def.icon];
                return (
                  <div
                    key={def.type}
                    className={styles.paletteItem}
                    draggable
                    onDragStart={(e) => handleDragStart(e, def.type)}
                  >
                    <GripVertical size={12} className={styles.gripIcon} />
                    {Icon && <Icon size={16} />}
                    <span>{def.name}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Canvas */}
        <div
          ref={canvasRef}
          className={styles.canvas}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onMouseDown={handleCanvasMouseDown}
          onMouseMove={handleCanvasMouseMove}
          onMouseUp={handleCanvasMouseUp}
          onMouseLeave={handleCanvasMouseUp}
        >
          <div
            className={styles.canvasContent}
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: '0 0',
            }}
          >
            {/* Connections SVG */}
            <svg className={styles.connectionsSvg}>{renderConnections()}</svg>

            {/* Nodes */}
            {nodes.map((node) => (
              <WorkflowNodeComponent
                key={node.id}
                node={node}
                isSelected={selectedNodeIds.has(node.id)}
                onSelect={handleNodeSelect}
                onMove={handleNodeMove}
                onMoveEnd={handleNodeMoveEnd}
                onDelete={handleNodeDelete}
                onConnectionStart={handleConnectionStart}
                onConnectionEnd={handleConnectionEnd}
                isConnecting={connectingFrom !== null}
                zoom={zoom}
              />
            ))}
          </div>

          {/* Marquee selection rectangle */}
          {marquee && (
            <div
              className={styles.marquee}
              style={{
                left: Math.min(marquee.startX, marquee.curX) * zoom + pan.x,
                top: Math.min(marquee.startY, marquee.curY) * zoom + pan.y,
                width: Math.abs(marquee.curX - marquee.startX) * zoom,
                height: Math.abs(marquee.curY - marquee.startY) * zoom,
              }}
            />
          )}

          {/* Empty state */}
          {nodes.length === 0 && (
            <div className={styles.emptyState}>
              <p>Drag nodes from the left panel to get started</p>
            </div>
          )}
        </div>

        {/* Config Panel */}
        {selectedNode && (
          <WorkflowNodeConfig
            node={selectedNode}
            onConfigChange={(config) => handleNodeConfigChange(selectedNode.id, config)}
            onNameChange={(name) => handleNodeNameChange(selectedNode.id, name)}
            onClose={() => setSelectedNodeIds(new Set())}
          />
        )}
      </div>
    </div>
  );
};
